import { useEffect, useState } from "react";
import { useAuth } from "react-oidc-context";
import { createTheme, ThemeProvider } from "@mui/material";

import Button from "@mui/material/Button";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";

import EditIcon from "@mui/icons-material/EditOutlined";
import { IOccupant } from "../../../../../types/ILocation";
import Occupants from "../../../../../components/occupants";
import { createBlankOccupant } from "../../../../../components/occupants/components/BlankOccupant";
import NitroService from "../../../../../utils/nitro.service";
import UnsavedConfirmation from "../../../../../components/unsavedConfirmation";
import { OccupantsArraySchema } from "../../../../addLocationPanel/schemas/OccupantSchema";
import { sortByField } from "../../../../../utils/Sort";
import { useNitroStore } from "../../../../../store/Store";
import CloseIcon from "../../../../../components/CloseIcon";
import LoadingIcon from "../../../../../components/Loading";
import PanelFooter from "../../../../../components/Panel/PanelFooter";
import { useNitroCAStore } from "../../../../../store/CompetitiveAnalysisStore";
import { getMarketerIdentifierFromProfile } from "../../../../../pages/home/favoriteOrigin/FavoriteOriginHelper";
import { saveAndRefreshFavoriteOriginPrices } from "../../../../../pages/home/favoriteOrigin/FavoriteOriginAddOriginUtils";
import { IFavoriteSetExtension } from "../../../../../types/IFavoriteOrigins";

const theme = createTheme({
  components: {
    MuiTable: {
      defaultProps: {
        size: "small",
        "aria-label": "a dense table",
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          borderWidth: 0,
        },
      },
    },
    MuiTableCell: {
      defaultProps: {
        padding: "none",
        align: "left",
      },
      styleOverrides: {
        root: {
          padding: "2px 0px",
          paddingRight: 30,
          borderWidth: 0,
        },
      },
    },
  },
});

const OccupantType = ({ types }: { types: string[] }) => {
  if (types.indexOf("Competitor") === -1 && types.indexOf("Koch") === -1) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 5,
          color: "#595959",
          fontWeight: 300,
          fontSize: 12,
        }}
      >
        <div
          style={{
            height: 12,
            width: 12,
            backgroundColor: "purple",
            borderRadius: 20,
          }}
        />
        Customer
      </div>
    );
  }

  if (types.indexOf("Competitor") > -1) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 5,
          color: "#595959",
          fontWeight: 300,
          fontSize: 12,
        }}
      >
        <div
          style={{
            height: 12,
            width: 12,
            backgroundColor: "#C52F00",
            borderRadius: 20,
          }}
        />
        Competitor
      </div>
    );
  }

  if (types.indexOf("Koch") > -1) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 5,
          color: "#595959",
          fontWeight: 300,
          fontSize: 12,
        }}
      >
        <div
          style={{
            height: 12,
            width: 12,
            backgroundColor: "blue",
            borderRadius: 20,
          }}
        />
        Koch
      </div>
    );
  }

  return <>{JSON.stringify(types)}</>;
};

const OverviewOccupantsTab = ({
  locationId,
  occupants,
  uom,
  reloadLocation,
}: {
  locationId: string;
  occupants: IOccupant[];
  uom: string;
  reloadLocation: () => Promise<void>;
}) => {
  const auth = useAuth();

  const [
    savedNotification,
    failedNotification,
    loadLocations,
    insightsEditing,
    setInsightsEditing,
    closeInsightsPanel,
    favoriteOrigins,
    triggerRefetchFavoriteOrigins,
  ] = useNitroStore((state) => [
    state.savedNotification,
    state.failedNotification,
    state.loadLocations,
    state.insightsEditing,
    state.setInsightsEditing,
    state.closeInsightsPanel,
    state.favoriteOrigins,
    state.triggerRefetchFavoriteOrigins,
  ]);
  const [troubleshootQuoteOpen, triggerRefetchQuotes, setInsightsPanelSaved] =
    useNitroCAStore((state) => [
      state.troubleshootQuoteOpen,
      state.triggerRefetchQuotes,
      state.setInsightsPanelSaved,
    ]);

  const [expandedDestinationId, setExpandedDestination] = useNitroCAStore(
    (state) => [state.expandedDestId, state.setExpandedDestination],
  );

  const [records, setRecords] = useState<IOccupant[]>(occupants);
  useEffect(() => {
    setRecords(occupants);
  }, [occupants, insightsEditing]);

  const addOccupant = () => {
    setRecords([...records, createBlankOccupant()]);
  };

  const extractProducts = (
    items: IOccupant[],
    targetType: "Koch" | "Competitor" | "Customer",
  ) => {
    const productSet = new Set<string>();
    items.forEach((occupant) => {
      if (occupant.types.includes(targetType)) {
        occupant.products.forEach((p) => {
          productSet.add(p.product.toUpperCase());
        });
      }
    });
    return productSet;
  };

  const [isSaving, setIsSaving] = useState(false);
  const saveOccupantUpdates = async () => {
    setIsSaving(true);
    try {
      const updatedOccupants = [...records].map((occupant) => {
        const updatedOccupant = {
          ...occupant,
          states: !occupant.types.includes("Customer") ? occupant.states : [],
          types: occupant.types.includes("Competitor")
            ? ["Competitor", "Customer"]
            : [...occupant.types],
        } as IOccupant;

        updatedOccupant.products = updatedOccupant.products.map((p) => {
          const updatedProduct = { ...p };
          if (!updatedProduct.quantity) {
            updatedProduct.quantity = 0;
          }
          return updatedProduct;
        });

        return updatedOccupant;
      });

      await NitroService.updateOccupants({
        id: locationId,
        occupants: updatedOccupants,
      });

      // Remove origins from the origin manager when no occupant of that side type retains the product.
      try {
        const originalKochProducts = extractProducts(occupants, "Koch");
        const originalCompetitorProducts = extractProducts(
          occupants,
          "Competitor",
        );

        const updatedKochProducts = extractProducts(records, "Koch");
        const updatedCompetitorProducts = extractProducts(
          records,
          "Competitor",
        );

        const kochRemovedProducts = new Set(
          [...originalKochProducts].filter((p) => !updatedKochProducts.has(p)),
        );
        const competitorRemovedProducts = new Set(
          [...originalCompetitorProducts].filter(
            (p) => !updatedCompetitorProducts.has(p),
          ),
        );

        if (
          kochRemovedProducts.size > 0 ||
          competitorRemovedProducts.size > 0
        ) {
          const marketer = getMarketerIdentifierFromProfile(
            auth.user?.profile as Record<string, unknown> | undefined,
          );
          if (marketer) {
            const saveTasks: Promise<IFavoriteSetExtension[]>[] = [];
            favoriteOrigins.forEach((favoriteOriginSet) => {
              const productUpper = favoriteOriginSet.product.toUpperCase();
              const isKochRemoved = kochRemovedProducts.has(productUpper);
              const isCompetitorRemoved =
                competitorRemovedProducts.has(productUpper);
              if (!isKochRemoved && !isCompetitorRemoved) return;

              const newFavorites = favoriteOriginSet.favoriteLocations.filter(
                (fav) => {
                  if (fav.location.id !== locationId) return true;
                  if (!fav.competitor && isKochRemoved) return false;
                  if (fav.competitor && isCompetitorRemoved) return false;
                  return true;
                },
              );

              if (
                newFavorites.length !==
                favoriteOriginSet.favoriteLocations.length
              ) {
                saveTasks.push(
                  saveAndRefreshFavoriteOriginPrices(
                    marketer,
                    favoriteOriginSet.product,
                    newFavorites,
                  ),
                );
              }
            });

            if (saveTasks.length > 0) {
              await Promise.all(saveTasks);
              triggerRefetchFavoriteOrigins();
            }
          }
        }
      } catch (originCleanupErr) {
        console.error(
          "Failed to clean up origin manager after product removal.",
          originCleanupErr,
        );
      }

      loadLocations({ panTo: true });
      await reloadLocation();
      setInsightsEditing(undefined);
      if (troubleshootQuoteOpen) {
        setInsightsPanelSaved(true);
        closeInsightsPanel();
      }
      savedNotification("Successfully saved occupants.");
      triggerRefetchQuotes();
      if (expandedDestinationId) {
        setExpandedDestination({ id: expandedDestinationId, name: "" });
      }
    } catch (err) {
      console.error(err);
      failedNotification("Failed to save occupants.", (err as any).message);
    }
    setIsSaving(false);
  };

  const [closeConfirmationOpen, setCloseConfirmationOpen] = useState(false);

  const [occupantsAreValid, setOccupantsAreValid] = useState(false);
  const [hasChange, setHasChange] = useState(false);
  useEffect(() => {
    const isValid = OccupantsArraySchema.isValidSync(records);

    OccupantsArraySchema.validate(records).catch((err) => {
      console.error(err);
    });

    // records.forEach((record) => {
    //   if (
    //     ["Koch", "Competitor"].includes(record.type) &&
    //     record.states.length === 0
    //   ) {
    //     isValid = false;
    //   }
    // });

    setOccupantsAreValid(isValid);

    let newhasChange = false;

    if (records.length !== occupants.length) {
      newhasChange = true;
    } else {
      records.forEach((occupant, index) => {
        const original = occupants[index];

        if (occupant.name !== original.name) {
          newhasChange = true;
        }
        if (
          occupant.types.length !== original.types.length ||
          !occupant.types.every((t) => original.types.includes(t))
        ) {
          newhasChange = true;
        }

        if (occupant.states !== original.states) {
          newhasChange = true;
        }

        if (occupant.products.length !== original.products.length) {
          newhasChange = true;
        } else {
          occupant.products.forEach((product, pIndex) => {
            const originalProduct = original.products[pIndex];
            if (product.product !== originalProduct.product) {
              newhasChange = true;
            }
            if (String(product.quantity) !== String(originalProduct.quantity)) {
              newhasChange = true;
            }
          });
        }
      });
    }

    setHasChange(newhasChange);
  }, [records, occupants]);

  return (
    <div
      style={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {closeConfirmationOpen && (
        <UnsavedConfirmation
          confirm={() => {
            setCloseConfirmationOpen(false);
            setInsightsEditing(undefined);
            if (troubleshootQuoteOpen) {
              closeInsightsPanel();
            }
          }}
          cancel={() => {
            setCloseConfirmationOpen(false);
          }}
        />
      )}

      <div
        className="insightsPanelPadding"
        style={{
          height: 32.5,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          paddingBottom: 10,
        }}
      >
        <div
          style={{
            color: "#0F2C52",
            fontWeight: 700,
            fontSize: 18,
            textTransform: "uppercase",
          }}
        >
          Occupants
        </div>
        {insightsEditing !== "Occupants" && (
          <Button
            id="occupantsEditButton"
            variant="contained"
            onClick={() => setInsightsEditing("Occupants")}
            style={{ display: "flex", gap: 4 }}
          >
            <EditIcon style={{ fontSize: 18 }} />
            Edit
          </Button>
        )}
        {!isSaving && insightsEditing === "Occupants" && (
          <CloseIcon
            onClick={() => {
              if (hasChange) {
                setCloseConfirmationOpen(true);
              } else {
                setInsightsEditing(undefined);
                if (troubleshootQuoteOpen) {
                  closeInsightsPanel();
                }
              }
            }}
          />
        )}
      </div>

      <div
        className="insightsPanelPadding"
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 20,
          paddingBottom: 20,
          flexGrow: 1,
          overflowY: "auto",
        }}
      >
        {(!records || records.length === 0) && (
          <div style={{ textAlign: "center", color: "#595959" }}>
            No data, what should i say here
          </div>
        )}
        {insightsEditing === "Occupants" && !isSaving && (
          <Occupants
            occupants={records}
            addOccupant={addOccupant}
            setOccupants={(r: IOccupant[]) => {
              setRecords(r);
            }}
            uom={uom || ""}
          />
        )}

        {insightsEditing === "Occupants" && isSaving && (
          <div
            style={{
              flexGrow: 1,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <LoadingIcon color="#005CA6" height={100} width={100} />
          </div>
        )}

        {insightsEditing !== "Occupants" &&
          records?.map((occupant, index) => {
            const total = occupant.products.reduce(
              (acc, product) => acc + product.quantity,
              0,
            );

            return (
              <div
                // eslint-disable-next-line react/no-array-index-key
                key={`Occupant-${index}`}
                style={{
                  border: "1px solid lightgray",
                  borderRadius: 4,
                  display: "flex",
                  flexDirection: "column",
                  gap: 20,
                  padding: "20px 0px ",
                }}
              >
                {/* HEADERS */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: "0px 20px",
                  }}
                >
                  <div
                    style={{
                      fontWeight: 700,
                      fontSize: 18,
                      color: "#0F2C52",
                      fontFamily: "TradeGothic LT Cond20",
                    }}
                  >
                    {occupant.name || "Unknown occupant"}
                  </div>

                  <div style={{ display: "flex", gap: 20 }}>
                    <OccupantType types={occupant.types || []} />
                  </div>
                </div>

                {/* BODY */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: "0px 20px",
                    flexGrow: 1,
                  }}
                >
                  <div>
                    <ThemeProvider theme={theme}>
                      <Table>
                        <TableBody>
                          {sortByField(
                            occupant.products,
                            "quantity",
                            "DESC",
                          )?.map((product) => (
                            <TableRow
                              key={`${occupant.name}-${product.product}`}
                            >
                              <TableCell
                                style={{
                                  color: "#393939",
                                  fontSize: 14,
                                  fontWeight: 700,
                                }}
                              >
                                {product.product}
                              </TableCell>

                              <TableCell
                                style={{
                                  color: "#393939",
                                  fontSize: 14,
                                  fontWeight: 400,
                                }}
                              >
                                {`${(product.quantity || 0).toLocaleString()} ${
                                  uom || ""
                                }`}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </ThemeProvider>
                  </div>

                  <div style={{ flexGrow: 1 }}>
                    <div
                      style={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        flexGrow: 1,
                        justifyContent: "flex-end",
                      }}
                    >
                      <div
                        style={{
                          color: "#595959",
                          fontSize: 14,
                          fontWeight: 700,
                          textAlign: "end",
                        }}
                      >
                        Total
                      </div>
                      <div
                        style={{
                          color: "#595959",
                          fontSize: 20,
                          fontWeight: 400,
                          textAlign: "end",
                        }}
                      >
                        {`${total.toLocaleString()} ${uom || ""}`}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
      </div>

      {insightsEditing === "Occupants" && (
        <PanelFooter>
          <Button
            id="occupantsSaveButton"
            variant="contained"
            fullWidth
            onClick={saveOccupantUpdates}
            disabled={!occupantsAreValid || !hasChange}
          >
            UPDATE OCCUPANTS
          </Button>
        </PanelFooter>
      )}
    </div>
  );
};

export default OverviewOccupantsTab;
